import React, { useEffect, useState } from "react";
import Modal from "react-responsive-modal";
import { getServicePlanList } from "../../models/_servicePlan";
import { activateLocationServicePlan, getLocationServicePlans } from "../../models/_location";
import { toast } from "react-hot-toast";
import moment from "moment";

type Props = {
    showModal: boolean;
    onClose: () => void;
    locationId: string;
    locationName?: string;
    onSuccess?: () => void;
};

export const AddAddOnModal: React.FC<Props> = ({
    showModal,
    onClose,
    locationId,
    locationName,
    onSuccess,
}) => {
    const [servicePlans, setServicePlans] = useState<any[]>([]);
    const [selectedPlanId, setSelectedPlanId] = useState<string>("");
    const [expiryDate, setExpiryDate] = useState<string>("");
    const [currentAddOns, setCurrentAddOns] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (showModal) {
            fetchServicePlans();
        }
    }, [showModal]);

    const fetchServicePlans = async () => {
        try {
            const [plans, existingAddOns] = await Promise.all([
                getServicePlanList(),
                getLocationServicePlans(locationId)
            ]);
            setServicePlans(plans || []);
            setCurrentAddOns(existingAddOns || []);
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Failed to fetch necessary details");
        }
    };

    const handleSubmit = async () => {
        if (!selectedPlanId || !expiryDate) {
            toast.error("Please fill in all fields");
            return;
        }

        setLoading(true);
        try {
            const res = await activateLocationServicePlan({
                location_id: locationId,
                service_plan_id: selectedPlanId,
                entry_date: moment().format("YYYY-MM-DD"),
                expiry_date: expiryDate,
            });

            if (res?.data?.activateLocationServicePlan) {
                toast.success("Add-on enrolled successfully");
                setSelectedPlanId("");
                setExpiryDate("");
                onClose();
                if (onSuccess) onSuccess();
            } else {
                console.log(res);
                const message = res?.errors?.[0]?.message;
                toast.error(message || "Failed to enroll add-on");
            }
        } catch (error) {
            console.log(error);
            console.error("Error enrolling add-on:", error);
            toast.error("An error occurred");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open={showModal}
            onClose={() => {
                onClose();
                setSelectedPlanId("");
                setExpiryDate("");
            }}
            center
            classNames={{
                root: "content-wrapper",
            }}
        >
            <div className="modal-header p-2 pb-4 mb-4">
                <h4 className="modal-title fs-1 mt-4">
                    Add-on Enrollment {locationName ? `- ${locationName}` : ""}
                </h4>
            </div>
            <div className="modal-body">
                <div className="row mb-4">
                    <label className="col-lg-6 col-md-6 fw-bold text-muted fs-6">
                        Pick an Add-on
                    </label>
                    <div className="col-lg-6 col-md-6 fv-row">
                        <select
                            className="form-select"
                            value={selectedPlanId}
                            onChange={(e) => {
                                const planId = e.target.value;
                                setSelectedPlanId(planId);
                                const plan = servicePlans.find(p => p.id === planId);
                                if (plan && plan.noOfDays) {
                                    // Find latest expiry date from active plans
                                    let baseDate = moment();
                                    if (currentAddOns.length > 0) {
                                        const activeAddOns = currentAddOns.filter(a => a.status === "ACTIVE");
                                        if (activeAddOns.length > 0) {
                                            const latestExpiry = moment.max(activeAddOns.map(a => moment(a.expiryDate)));
                                            if (latestExpiry.isAfter(moment())) {
                                                baseDate = latestExpiry;
                                            }
                                        }
                                    }
                                    setExpiryDate(baseDate.add(plan.noOfDays, 'days').format("YYYY-MM-DD"));
                                } else {
                                    setExpiryDate("");
                                }
                            }}
                            disabled={loading}
                        >
                            <option value="">Select a Add-On</option>
                            {servicePlans.map((plan) => (
                                <option key={plan.id} value={plan.id}>
                                    {plan.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="row mb-4">
                    <label className="col-lg-6 col-md-6 fw-bold text-muted fs-6">
                        Expiration Date
                    </label>
                    <div className="col-lg-6 col-md-6 fv-row">
                        <input
                            type="date"
                            className="form-control"
                            value={expiryDate}
                            onChange={(e) => setExpiryDate(e.target.value)}
                            disabled={loading}
                        />
                    </div>
                </div>
            </div>
            <div className="modal-footer mt-6 p-0 justify-content-start">
                <button
                    type="button"
                    className="btn btn-primary me-4 fs-4 p-0 px-6 py-3 mt-6"
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading ? "Processing..." : "OK"}
                </button>
                <button
                    type="button"
                    className="btn btn-secondary me-4 fs-4 p-0 px-6 py-3 mt-6"
                    onClick={onClose}
                    disabled={loading}
                >
                    Cancel
                </button>
            </div>
        </Modal>
    );
};
